import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee, STATUS_LABEL } from "@/lib/format";
import { ShoppingCart, Package, Users, IndianRupee, Clock, AlertTriangle, Loader2 } from "lucide-react";

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient">
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-full ${tone}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <p className="mt-4 font-heading text-2xl font-bold text-ink">{value}</p>
      <p className="text-sm text-ink-muted">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  usePageMeta("Admin Dashboard");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats)
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Dashboard</h1>
      <p className="mt-1 text-ink-soft">Overview of your store performance.</p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={ShoppingCart} label="Total Orders" value={stats.total_orders} tone="bg-brand/10 text-brand" />
        <StatCard icon={Clock} label="Active Orders" value={stats.pending_orders} tone="bg-amber-100 text-amber-600" />
        <StatCard icon={IndianRupee} label="Revenue (delivered)" value={rupee(stats.revenue)} tone="bg-sage/15 text-sage" />
        <StatCard icon={Users} label="Customers" value={stats.total_customers} tone="bg-blue-100 text-blue-600" />
        <StatCard icon={Package} label="Active Products" value={`${stats.active_products}/${stats.total_products}`} tone="bg-brand/10 text-brand" />
        <StatCard icon={AlertTriangle} label="Low Stock (≤5)" value={stats.low_stock} tone="bg-red-100 text-red-600" />
      </div>

      <div className="mt-8 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg text-ink">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm font-semibold text-brand hover:underline">View all</Link>
        </div>
        {stats.recent_orders.length === 0 ? (
          <p className="mt-6 text-center text-sm text-ink-muted">No orders yet.</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-ink">{o.order_number}</p>
                  <p className="text-xs text-ink-muted">{o.customer_name} · {o.customer_mobile}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-ink">{rupee(o.total)}</p>
                  <p className="text-xs text-brand">{STATUS_LABEL[o.status]}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
